import { useCallback, useEffect, useState } from "react";
import apiClient from "@/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface UseCachedTableStateWithUrlOptions {
	queryKey: readonly string[];
	endpoint: string;
}

interface UseCachedTableStateWithUrlReturn<T> {
	items: T[];
	isLoading: boolean;
	isPending: boolean;
	totalItems: number;
	tableQuery: string;
	setTableQuery: (query: string) => void;
	refetchData: (query?: string) => void;
	invalidateCache: () => void;
}

export function useCachedTableStateWithUrl<T = unknown>({
	queryKey,
	endpoint,
}: UseCachedTableStateWithUrlOptions): UseCachedTableStateWithUrlReturn<T> {
	const [tableQuery, setTableQuery] = useState("");
	const [totalItems, setTotalItems] = useState(0);
	const [isInitialized, setIsInitialized] = useState(false);

	const queryClient = useQueryClient();

	const { data, refetch, isPending } = useQuery({
		queryKey: [...queryKey, { query: tableQuery }],
		queryFn: async () => {
			const queryString = endpoint.includes("?") ? `${endpoint}&${tableQuery}` : `${endpoint}?${tableQuery}`;

			let response: any;

			try {
				response = await apiClient.get(queryString);
			} catch (error) {
				toast.error((error as any)?.response?.data?.message || "Something went wrong");
			}

			const total = response?.data?.total > 0 ? response?.data?.total : Math.max(response?.data?.length || 0, 0);

			setTotalItems(total);
			return response.data;
		},
		enabled: isInitialized,
	});

	useEffect(() => {
		if (!isInitialized) {
			setIsInitialized(true);
		}
	}, [isInitialized]);

	useEffect(() => {
		const cached = queryClient.getQueryData<any>([...queryKey, { query: tableQuery }]);

		if (cached) {
			const total = cached.total ?? cached.data?.length ?? cached.length ?? 0;
			setTotalItems(total);
		}
	}, [queryClient, queryKey, tableQuery]);

	const refetchData = useCallback(
		(query?: string) => {
			if (query?.length) {
				setTableQuery(query);
				setIsInitialized(true);
			} else {
				setIsInitialized(true);
				refetch();
			}
		},
		[refetch]
	);

	const invalidateCache = useCallback(() => {
		queryClient.invalidateQueries({ queryKey });
	}, [queryClient, queryKey]);

	return {
		items: data?.data || data || [],
		// isLoading: isLoading || !isInitialized || isFetching || isPending,
		isLoading: isPending,
		isPending: isPending,
		totalItems: totalItems || data?.total || data?.data?.length || data?.length || 0,
		tableQuery,
		setTableQuery,
		refetchData,
		invalidateCache,
	};
}
